import { useContext, useEffect, useRef, useState } from "react";
import { useForm, useFormState } from "react-hook-form";

import { AppContext } from "../../../../core/context/appContextProvider";
import { addConversionTransaction, addInvestmentTransaction, addMultipleValuationTransaction, addSaleTransaction, addStockSplitTransaction, addValuationTransaction, deleteConversionTransaction, deleteInvestmentTransactionPurchase, deleteSaleTransaction, deleteStockSplitTransaction, deleteValuationTransaction, getAllValuationTransactions, getInvestmentTransaction, getInvestmentTransactionNameAvailability, getInvestmentTransactions, getInvestmentTransactionsList, updateConversionTransaction, updateInvestmentTransaction, updateSaleTransaction, updateStockSplitTransaction, updateValuationTransaction } from "../../../../services/investmentTransaction.service";
import { GENERIC_ERROR_MESSAGE } from "../../../../utils/constants/text.constants";
import { DateTimeFormat } from "../../../../utils/helpers/format.helper";
import { useEffectAsync } from "../../../../utils/hooks/useEffectAsync.hook";
import { AddNewButtonOptions } from "../../../../utils/types/common.type";
import { InvestmentTransaction, InvestmentTransactionCRUDStatus, InvestmentTransactionDetailsType, InvestmentTransactionFollowing, InvestmentTransactionNewView, InvestmentTransactionPurchase, SelectedInvestmentTransaction } from "../../../../utils/types/investmentTransaction.type";
import { FilterItem } from "../../../../utils/types/listItems";
import { PortfolioCompanyDetailsType } from "../../../../utils/types/portfolioCompany.type";
import {
  CREATE_INVESTMENT_TRANSACTION_ERROR,
  CREATE_INVESTMENT_TRANSACTION_SUCCESS,
  DELETE_INVESTMENT_TRANSACTION_ERROR,
  DELETE_INVESTMENT_TRANSACTION_SUCCESS,
  ERROR_TAKEN_INVESTMENT_TRANSACTION_NAME,
  GET_INVESTMENT_TRANSACTION_DETAILS_ERROR,
  GET_INVESTMENT_TRANSACTION_LIST_ERROR,
  GET_PURCHASE_TRANSACTION_LIST_ERROR,
  INVESTMENT_TRANSACTION_FOLLOWING_FORM_DEFAULT_VALUE,
  INVESTMENT_TRANSACTION_PURCHASE_FORM_DEFAULT_VALUE,
  NEW_INVESTMENT_TRANSACTION_PURCHASE_ONLY,
  NEW_INVESTMENT_TRANSACTION_TYPES,
  UPDATE_INVESTMENT_TRANSACTION_ERROR,
  UPDATE_INVESTMENT_TRANSACTION_SUCCESS
} from "../InvestmentTransactionList.constants";

type Props = {
  investmentTransaction?: any;
  onClose: Function;
  fetchAllInvestmentTransactions: Function;
  isNewInvestmentTransaction: boolean;
  selectedInvestmentTransaction: SelectedInvestmentTransaction;
  setSelectedInvestmentTransaction: Function;
  setSelectedPortfolioCompany: Function;
  viewPurchaseTransactionId?: string;
  fundId?: string;
}

export const useInvestmentTransactionDetails = ({
  investmentTransaction,
  onClose,
  fetchAllInvestmentTransactions,
  isNewInvestmentTransaction,
  selectedInvestmentTransaction,
  setSelectedInvestmentTransaction,
  setSelectedPortfolioCompany,
  viewPurchaseTransactionId,
  fundId
}: Props) => {
  const [loading, setLoading] = useState<InvestmentTransactionCRUDStatus>();
  const [toBeDeleted, setToBeDeleted] = useState<string | undefined>();
  const [showExitConfirmation, setShowExitConfirmation] = useState<boolean>(false);
  const [showApplyMultipleConfirmation, setShowApplyMultipleConfirmation] = useState<boolean>(false);
  const [matchingPurchaseTransactionsText, setMatchingPurchaseTransactionsText] = useState<string>('');
  const [matchingPurchaseTransactionsList, setMatchingPurchaseTransactionsList] = useState<any[]>();
  const [investmentTransactionList, setInvestmentTransactionList] = useState<any[]>();
  const [investmentTransactionType, setInvestmentTransactionType] = useState<string>('');
  const [selectedPurchaseTransactionId, setSelectedPurchaseTransactionId] = useState<string>();
  const [initialPurchaseTransaction, setInitialPurchaseTransaction] = useState<any>();
  const [latestValuationAmount, setLatestValuationAmount] = useState<number>(0);
  const [newValuationTransaction, setNewValuationTransaction] = useState<any>();
  const [transactionNameUnavailableError, setTransactionNameUnavailableError] = useState<boolean>(false);
  const [isConverted, setIsConverted] = useState<boolean>(false);
  const [isNewButtonVisible, setIsNewButtonVisible] = useState<boolean>(false);
  const [newInvestmentTransactionTypeList, setNewInvestmentTransactionTypeList] = useState<FilterItem[]>([]);
  const [originalInvestmentTransaction, setOriginalInvestmentTransaction] = useState<any>();
  
  const {
    state,
    informationAlert
  } = useContext(AppContext);
  const clientId = state.loginUser.clientId;

  const {
    register,
    handleSubmit,
    setError,
    formState: {
      errors
    },
    control,
    watch,
    reset,
    setValue,
  } = useForm<InvestmentTransactionPurchase>({
    defaultValues: investmentTransaction,
  });
  const {
    isDirty
  } = useFormState({
    control
  });
  //  Other Investment Transaction
  const {
    register: register2,
    handleSubmit: handleSubmit2,
    formState: {
      errors: errors2
    },
    control: control2,
    watch: watch2,
    reset: reset2,
    resetField: resetField2,
    setValue: setValue2,
  } = useForm<InvestmentTransactionFollowing>({
    defaultValues: investmentTransaction ?? INVESTMENT_TRANSACTION_FOLLOWING_FORM_DEFAULT_VALUE
  });
  const {
    isDirty: isDirty2
  } = useFormState({
    control: control2
  });

  const fetchPurchaseTransaction = async (id: string, isCanceled?: () => boolean) => {
    setLoading(InvestmentTransactionCRUDStatus.Loading);
    try {
      if (isCanceled?.()) return;
      const purchaseTransactionResponse = await getInvestmentTransaction(id);

      if (purchaseTransactionResponse) {
        setInitialPurchaseTransaction(purchaseTransactionResponse);

        if (isNewInvestmentTransaction) {
          await presetNewFormValues(investmentTransactionType, purchaseTransactionResponse);
        }
      }

    } catch (e) {
      informationAlert(GET_INVESTMENT_TRANSACTION_DETAILS_ERROR, "error");
    }
    setLoading(undefined);
  };

  const fetchTransactionList = async (fromTransactionId: string, isCanceled?: () => boolean) => {
    setLoading(InvestmentTransactionCRUDStatus.Loading);
    try {
      if (isCanceled?.()) return;
      const investmentTransactionList = await getInvestmentTransactionsList(fromTransactionId!);

      // check if conversion does not exist, enable new button
      if (!investmentTransactionList.items.find((item: any) => item.investmentTransactionType === 'CONVERSION')) {
        setIsNewButtonVisible(true);
      }

      const updatedInvestmentTransactionList = investmentTransactionList.items.reduce((filteredArray: any[], followingTransaction: InvestmentTransaction) => {
        followingTransaction.fromTransactionId = fromTransactionId as string;

        if (investmentTransaction.investmentTransactionType === 'PURCHASE') {
          // display all if current is purchase or conversion
          followingTransaction.fromTransaction = investmentTransaction;
          filteredArray.push(followingTransaction);
        }
        else if (investmentTransaction.investmentTransactionType === 'CONVERSION') {
          // display all if current is purchase or conversion
          filteredArray.push(followingTransaction);
        } else if (investmentTransaction.investmentTransactionType === followingTransaction.investmentTransactionType
          && investmentTransaction.id !== followingTransaction.id) {
          // pass along origin transaction
          if (investmentTransaction.fromTransaction && investmentTransaction.fromTransaction.id) {
            followingTransaction.fromTransaction = investmentTransaction.fromTransaction;
          }
          // display transactions only of same type if current is following, excluding current
          filteredArray.push(followingTransaction);
        }

        // check if conversion exists, then remove it from the list so user can't book another conversion
        if (followingTransaction.investmentTransactionType === 'CONVERSION') {
          setIsConverted(true);
        }

        return filteredArray;
      }, []);

      const sorted = updatedInvestmentTransactionList.sort((a: any, b: any) => b.date - a.date).reverse();

      setInvestmentTransactionList(sorted);
    } catch (e) {
      informationAlert(GET_INVESTMENT_TRANSACTION_LIST_ERROR, "error");
    }
    setLoading(undefined);
  };

  useEffect(() => {
    if (investmentTransaction && investmentTransaction.id && !isNewInvestmentTransaction) {
      setInvestmentTransactionType(investmentTransaction.investmentTransactionType);
    }
  }, []);

  useEffect(() => {
    if (isNewInvestmentTransaction) {
      if (selectedInvestmentTransaction.newView === InvestmentTransactionNewView.Purchase) {
        setNewInvestmentTransactionTypeList(NEW_INVESTMENT_TRANSACTION_PURCHASE_ONLY);
      } else if (selectedInvestmentTransaction.newView === InvestmentTransactionNewView.ConversionExcluded) {
        const currentList = NEW_INVESTMENT_TRANSACTION_TYPES.filter(type => type.id !== 'CONVERSION');

        setNewInvestmentTransactionTypeList(currentList);
      } else {
        setNewInvestmentTransactionTypeList(NEW_INVESTMENT_TRANSACTION_TYPES);
      }
    }
  }, [selectedInvestmentTransaction]);

  useEffectAsync(async (isCanceled) => {
    if (investmentTransaction && investmentTransaction.id && !isNewInvestmentTransaction) {
      setInvestmentTransactionType(investmentTransaction.investmentTransactionType);

      let purchaseId: string;
      let fromTransactionId: string;

      if (investmentTransaction.investmentTransactionType === 'PURCHASE') {
        purchaseId = investmentTransaction.id;
      } else {
        purchaseId = investmentTransaction.fromTransactionId;
        if (investmentTransaction.fromTransaction && investmentTransaction.fromTransaction.id) {
          setInitialPurchaseTransaction(investmentTransaction.fromTransaction);
        } else {
          await fetchPurchaseTransaction(purchaseId, isCanceled);
        }
      }

      if (investmentTransaction.investmentTransactionType === 'PURCHASE') {
        fromTransactionId = purchaseId;
      } else if (investmentTransaction.investmentTransactionType === 'CONVERSION') {
        fromTransactionId = investmentTransaction.id;
      } else {
        fromTransactionId = investmentTransaction.fromTransactionId;
      }

      await fetchTransactionList(fromTransactionId!, isCanceled);

      presetUpdateFormValues();
    }

    if (isNewInvestmentTransaction && viewPurchaseTransactionId) {
      setSelectedPurchaseTransactionId(viewPurchaseTransactionId);
    }
  }, [investmentTransaction]);

  useEffectAsync(async (isCanceled) => {
    if (isNewInvestmentTransaction && selectedPurchaseTransactionId) {
      await fetchPurchaseTransaction(selectedPurchaseTransactionId, isCanceled);
    }
  }, [selectedPurchaseTransactionId]);

  useEffect(() => {
    reset(investmentTransaction);
    setTransactionNameUnavailableError(false);
  }, [investmentTransaction, reset]);

  const presetNewFormValues = async (transactionType: string, fromTransaction: any) => {
    switch (transactionType) {
      case "SALE_FULL": {
        setValue2("quantityOther", fromTransaction.quantity);
        setValue2("amountPerQuantityOther", '');
        setValue2("totalAmountOther", '');
        break;
      }
      case "SALE_PARTIAL": {
        setValue2("quantityOther", '');
        setValue2("amountPerQuantityOther", '');
        setValue2("totalAmountOther", '');
        break;
      }
      case "NET_EFFECT_VALUATION": {
        setValue2("totalAmountOther", '');
        setValue2("amountPerQuantityOther", '');
        setValue2("quantityOther", fromTransaction.quantity);
        setValue2("newTotalAmount", null);
        setValue2("newAmountPerQuantity", null);

        if (selectedPurchaseTransactionId) {
          const valuationsResponse = await getAllValuationTransactions(selectedPurchaseTransactionId);

          if (valuationsResponse && valuationsResponse.items.length !== 0) {
            const latestValuation = valuationsResponse.items[0];

            setLatestValuationAmount(latestValuation.totalAmount);
          } else {
            setLatestValuationAmount(fromTransaction.totalAmount);
          }
        }
        break;
      }
      case "TOTAL_VALUATION": {
        setValue2("totalAmountOther", '');
        setValue2("amountPerQuantityOther", '');
        setValue2("quantityOther", fromTransaction.quantity);
        break;
      }
      case "WRITE_OFF": {
        setValue2("totalAmountOther", 0);
        setValue2("quantityOther", fromTransaction.quantity);
        break;
      }
      case "SPLIT": {
        setValue2("quantityOther", '');
        setValue2("totalAmountOther", fromTransaction.totalAmount);
        break;
      }
      case "CONVERSION": {
        setValue2("quantityOther", '');
        setValue2("totalAmountOther", '');
        setValue2("amountPerQuantityOther", '');
        break;
      }
    }
  };

  const presetUpdateFormValues = () => {
    setValue2("quantityOther", investmentTransaction.quantity);
    if (investmentTransaction.amountPerQuantity) {
      setValue2("amountPerQuantityOther", investmentTransaction.amountPerQuantity);
    }
    setValue2("totalAmountOther", investmentTransaction.totalAmount);
    setValue2("date", investmentTransaction.date);
    switch (investmentTransaction.investmentTransactionType) {
      case "SPLIT": {
        setValue2("amountPerQuantityOther", investmentTransaction.previousCostPerQuantity);
        setValue2("totalAmountOther", investmentTransaction.previousCostPerQuantity * investmentTransaction.previousQuantity);
        break;
      }
      case "SALE": {
        setValue2("removeFromHistoricalSoi", investmentTransaction.removeFromHistoricalSoi ? investmentTransaction.removeFromHistoricalSoi : false);
        break;
      }
      case "CONVERSION": {
        setValue2("newSecurityType", investmentTransaction.securityType ? investmentTransaction.securityType : '');
        setValue2("newInvestmentTranche", investmentTransaction.investmentTranche ? investmentTransaction.investmentTranche : '');
        setValue2("newCommitmentAmount", investmentTransaction.commitmentAmount ? investmentTransaction.commitmentAmount : '');
        setValue2("newNoteAgreement", investmentTransaction.noteAgreement ? investmentTransaction.noteAgreement : '');
        setValue2("newInterestRate", investmentTransaction.interestRate ? investmentTransaction.interestRate : '');
        break;
      }
      // case "VALUATION": {
      // if (investmentTransaction.entryType === 'NET_EFFECT_VALUATION') {
      //   const valuationsResponse = await getAllValuationTransactions(investmentTransaction.id);

      //   if (valuationsResponse && valuationsResponse.items.length > 0) {
      //     const currentValuation = valuationsResponse.items[0];

      //     setLatestValuationAmount(latestValuation.totalAmount);
      //   } else {
      //     setLatestValuationAmount(initialPurchaseTransaction.totalAmount);
      //   }

      // setValue2("amountPerQuantityOther", investmentTransaction.previousCostPerQuantity);
      // setValue2("totalAmountOther", investmentTransaction.previousCostPerQuantity * investmentTransaction.previousQuantity);
      // break;
      // }
    }
  };

  const checkMatchingPortcoSecurityTransactions = async (purchaseTransaction: any) => {
    setLoading(InvestmentTransactionCRUDStatus.Loading);
    try {
      const purchaseTransactionListResponse = await getInvestmentTransactions();

      const matchingPurchaseTransactions = purchaseTransactionListResponse.items.filter((purchase: any) => (purchase.portfolioCompany.id === purchaseTransaction.portfolioCompany.id &&
        purchase.securityType === purchaseTransaction.securityType && purchase.id !== purchaseTransaction.id)
      );

      return matchingPurchaseTransactions;
    } catch (e) {
      informationAlert(GET_PURCHASE_TRANSACTION_LIST_ERROR, "error");
    }
    setLoading(undefined);
  };

  const createPurchaseTransaction = async (data: InvestmentTransaction) => {
    try {
      await addInvestmentTransaction(data);
      fetchAllInvestmentTransactions();
      informationAlert(CREATE_INVESTMENT_TRANSACTION_SUCCESS, "success");
      closeDrawer();
    } catch (error) {
      informationAlert(CREATE_INVESTMENT_TRANSACTION_ERROR, "error");
    }
  };

  const editPurchaseTransaction = async (data: InvestmentTransaction) => {
    if (data.id) {
      try {
        await updateInvestmentTransaction(data, data.id);

        fetchAllInvestmentTransactions();
        informationAlert(UPDATE_INVESTMENT_TRANSACTION_SUCCESS, "success");
        closeDrawer();
      } catch (error) {
        informationAlert(UPDATE_INVESTMENT_TRANSACTION_ERROR, "error");
      }
    }
  };

  const createUpdatePurchaseTransaction = async (data: InvestmentTransaction) => {
    if (data.name) {
      data.name = data.name.trim();

      if (isNewInvestmentTransaction || investmentTransaction.name !== data.name) {
        const investmentTransactionNameIsAvailable = await validateTransactionName(data.name);

        if (!investmentTransactionNameIsAvailable) {
          setTransactionNameUnavailableError(true);
          return;
        }
      }
    }

    let resultData = {
      ...data,
      tenantId: clientId,
      portfolioCompanyId: data.portfolioCompany?.id,
      fundId: data.fund?.id,
      costPerQuantity: data.quantity !== '0' ? data.amountPerQuantity : 0,
      totalCost: data.totalAmount
    };

    if (isNewInvestmentTransaction) {

      resultData = {
        ...(isNewInvestmentTransaction ? INVESTMENT_TRANSACTION_PURCHASE_FORM_DEFAULT_VALUE : {}),
        ...resultData
      };

      setLoading(InvestmentTransactionCRUDStatus.Adding);
      await createPurchaseTransaction(resultData);
    } else {

      setLoading(InvestmentTransactionCRUDStatus.Updating);
      await editPurchaseTransaction(resultData);
    }

    setLoading(undefined);
  };

  const createInvestmentTransaction = async (transactionType: string, data: any) => {
    try {
      switch (transactionType) {
        case 'SALE_PARTIAL':
        case 'SALE_FULL':
        case "WRITE_OFF":
          await addSaleTransaction(data, data.investmentTransaction.id);
          break;
        case 'TOTAL_VALUATION':
        case "NET_EFFECT_VALUATION": {
          const matchingPurchaseTransactions = await checkMatchingPortcoSecurityTransactions(initialPurchaseTransaction);

          if (matchingPurchaseTransactions && matchingPurchaseTransactions.length > 0) {
            let text = '';

            matchingPurchaseTransactions.map(purchase => {
              text += `${purchase.name} - ${purchase.fund?.name} - ${purchase.portfolioCompany?.name} - ${purchase.securityType} ${'\n'} `;
            });

            setMatchingPurchaseTransactionsList(matchingPurchaseTransactions);
            setMatchingPurchaseTransactionsText(text);
            setNewValuationTransaction(data);
            setShowApplyMultipleConfirmation(true);
          } else {
            await addValuationTransaction(data, data.investmentTransaction.id);
            informationAlert(CREATE_INVESTMENT_TRANSACTION_SUCCESS, "success");
            closeDrawer();
          }

          break;
        }
        case "SPLIT":
          await addStockSplitTransaction(data, data.investmentTransaction.id);
          break;
        case "CONVERSION":
          await addConversionTransaction(data, data.investmentTransaction.id);
          break;
      }
      if (transactionType !== 'TOTAL_VALUATION' && transactionType !== 'NET_EFFECT_VALUATION') {
        informationAlert(CREATE_INVESTMENT_TRANSACTION_SUCCESS, "success");
        closeDrawer();
      }
    } catch (error) {
      informationAlert(CREATE_INVESTMENT_TRANSACTION_ERROR, "error");
    }
  };

  const editInvestmentTransaction = async (transactionType: string, data: any) => {
    if (investmentTransaction.id) {
      try {
        switch (transactionType) {
          case 'SALE':
          case "WRITE_OFF":
            await updateSaleTransaction(data, investmentTransaction.fromTransactionId, investmentTransaction.id);
            break;
          case 'VALUATION':
            await updateValuationTransaction(data, investmentTransaction.fromTransactionId, investmentTransaction.id);
            break;
          case "SPLIT":
            await updateStockSplitTransaction(data, investmentTransaction.fromTransactionId, investmentTransaction.id);
            break;
          case "CONVERSION":
            await updateConversionTransaction(data, investmentTransaction.fromTransactionId);
            break;
        }
        informationAlert(UPDATE_INVESTMENT_TRANSACTION_SUCCESS, "success");
        closeDrawer();
      } catch (error) {
        informationAlert(UPDATE_INVESTMENT_TRANSACTION_ERROR, "error");
      }
    }
  };

  const createUpdateInvestmentTransaction = async (data: InvestmentTransactionFollowing) => {
    let resultData = {};

    switch (investmentTransactionType) {
      case 'SALE_PARTIAL':
        resultData = {
          removeFromHistoricalSoi: false,
          totalAmount: data.totalAmountOther,
          saleType: 'PARTIAL',
          quantity: data.quantityOther
        };
        break;
      case 'SALE_FULL':
        resultData = {
          removeFromHistoricalSoi: data.removeFromHistoricalSoi,
          totalAmount: data.totalAmountOther,
          saleType: 'FULL',
          quantity: data.quantityOther
        };
        break;
      case "WRITE_OFF": {
        resultData = {
          removeFromHistoricalSoi: data.removeFromHistoricalSoi,
          totalAmount: data.totalAmountOther,
          saleType: 'WRITE_OFF',
          quantity: data.quantityOther
        };
        break;
      }
      case "SALE": {
        resultData = {
          removeFromHistoricalSoi: data.removeFromHistoricalSoi,
          totalAmount: data.totalAmountOther,
          saleType: investmentTransaction.saleType,
          quantity: data.quantityOther
        };
        break;
      }
      case 'TOTAL_VALUATION':
        resultData = {
          investmentTransactionType: 'VALUATION',
          amountPerQuantity: data.amountPerQuantityOther,
          totalAmount: data.totalAmountOther,
          entryType: 'TOTAL_VALUATION',
          quantity: data.quantityOther,
        };
        break;
      case 'NET_EFFECT_VALUATION':
        resultData = {
          investmentTransactionType: 'VALUATION',
          amountPerQuantity: data.newAmountPerQuantity,
          totalAmount: data.newTotalAmount,
          entryType: 'NET_EFFECT_VALUATION',
          quantity: data.quantityOther,
        };
        break;
      case 'VALUATION':
        // if (investmentTransaction.entryType === 'NET_EFFECT_VALUATION') {
        //   data.amountPerQuantityOther = (latestValuationAmount + data.totalAmountOther) / initialPurchaseTransaction.quantity;
        //   data.totalAmountOther = (latestValuationAmount + data.totalAmountOther);
        // }
        resultData = {
          investmentTransactionType: 'VALUATION',
          amountPerQuantity: data.amountPerQuantityOther,
          totalAmount: data.totalAmountOther,
          entryType: investmentTransaction.entryType,
          quantity: data.quantityOther,
        };
        break;
      case "SPLIT": {
        resultData = {
          quantity: data.quantityOther,
        };
        break;
      }
      case "CONVERSION": {
        resultData = {
          quantity: data.quantityOther,
          totalConvertedCost: data.totalAmountOther,
          costPerQuantity: data.quantityOther !== '0' ? data.amountPerQuantityOther : 0,
          securityType: data.newSecurityType,
          interestRate: data.newInterestRate,
          commitmentAmount: data.newCommitmentAmount,
          noteAgreement: data.newNoteAgreement,
          investmentTranche: data.newInvestmentTranche
        };
        break;
      }
    }

    if (isNewInvestmentTransaction) {
      resultData = {
        ...resultData,
        tenantId: clientId,
        date: DateTimeFormat.noMilliseconds(data.date as Date),
        investmentTransaction: {
          id: selectedPurchaseTransactionId,
        },
      };
      setLoading(InvestmentTransactionCRUDStatus.Adding);
      await createInvestmentTransaction(investmentTransactionType, resultData);
    } else {
      resultData = {
        ...resultData,
        tenantId: clientId,
        date: DateTimeFormat.noMilliseconds(typeof data.date === 'object' ? data.date as Date : new Date(data.date as string)),
        investmentTransaction: {
          id: investmentTransaction.fromTransactionId,
        },
        id: investmentTransaction.id,
      };
      setLoading(InvestmentTransactionCRUDStatus.Updating);

      await editInvestmentTransaction(investmentTransactionType, resultData);
    }

    setLoading(undefined);
  };

  const validateTransactionName: any = async (value: string) => {
    if (value) {
      try {
        return await getInvestmentTransactionNameAvailability(value);
      } catch (e) {
        informationAlert(GENERIC_ERROR_MESSAGE, "error");
      }
    }
  };

  const handleInvestmentTransactionNameValidation = async (value: string) => {
    if (value) {
      value = value.trim();

      if (isNewInvestmentTransaction || (investmentTransaction && value !== investmentTransaction.name)) {
        const investmentTransactionNameIsAvailable = await validateTransactionName(value);

        investmentTransactionNameIsAvailable ? setTransactionNameUnavailableError(false) : setTransactionNameUnavailableError(true);
      } else {
        setTransactionNameUnavailableError(false);
      }
    }
  };

  const toggleDrawer = () => {
    if (isDirty) {
      setShowExitConfirmation(true);
    } else {
      closeDrawer();
    }
  };

  const closeDrawer = () => {
    reset();
    onClose();
    setShowExitConfirmation(false);
  };

  const keepDrawerOpen = () => {
    setShowExitConfirmation(false);
  };

  const handleDelete = () => {
    setInvestmentTransactionType(investmentTransaction?.investmentTransactionType);
    setToBeDeleted(investmentTransaction?.id ?? '');
  };

  const handleGoBack = () => {
    initialPurchaseTransaction.fromTransaction 
    ?
      setSelectedInvestmentTransaction({
      investmentTransactionType: "PURCHASE",
      investmentTransaction: originalInvestmentTransaction.investmentTransaction,
      viewPurchaseTransactionId: originalInvestmentTransaction.investmentTransaction,
      type: InvestmentTransactionDetailsType.Edit
    })
    :
      setSelectedInvestmentTransaction({
      investmentTransactionType: "PURCHASE",
      investmentTransaction:  initialPurchaseTransaction,
      viewPurchaseTransactionId: initialPurchaseTransaction.id,
      type: InvestmentTransactionDetailsType.Edit
    });
  };

  const handleCancelDelete = () => {
    setToBeDeleted(undefined);
  };

  const handleChangeTransactionType = async (transactionType: any) => {
    setInvestmentTransactionType(transactionType);
    if (initialPurchaseTransaction) {
      await presetNewFormValues(transactionType, initialPurchaseTransaction);
    }
  };

  const handleViewTransactionDetails = (investmentTransaction: any) => {
    setSelectedInvestmentTransaction({
      investmentTransaction,
      type: InvestmentTransactionDetailsType.Edit
    });
    if(investmentTransaction.fromTransaction){
        setOriginalInvestmentTransaction({
        investmentTransaction: investmentTransaction.fromTransaction,
        type: InvestmentTransactionDetailsType.Edit
      });
    }
  };

  const handleAddSingleValuation = async () => {
    try {
      await addValuationTransaction(newValuationTransaction, newValuationTransaction.investmentTransaction.id);
      setShowApplyMultipleConfirmation(false);
      informationAlert(UPDATE_INVESTMENT_TRANSACTION_SUCCESS, "success");
      closeDrawer();
    } catch (error) {
      informationAlert(CREATE_INVESTMENT_TRANSACTION_ERROR, "error");
    }
  };

  const handleAddMultipleValuation = async () => {
    try {
      const multipleValuations: any[] = [];

      multipleValuations.push(newValuationTransaction);

      matchingPurchaseTransactionsList?.map(purchase => {
        const newMatchingValuation = {
          ...newValuationTransaction,
          investmentTransaction: {
            id: purchase.id
          },
          totalAmount: purchase.quantity * newValuationTransaction.amountPerQuantity,
          quantity: purchase.quantity
        };

        multipleValuations.push(newMatchingValuation);
      });

      await addMultipleValuationTransaction(multipleValuations);

      setNewValuationTransaction(undefined);
      setShowApplyMultipleConfirmation(false);
      informationAlert(UPDATE_INVESTMENT_TRANSACTION_SUCCESS, "success");
      closeDrawer();
    } catch (error) {
      informationAlert(CREATE_INVESTMENT_TRANSACTION_ERROR, "error");
    }
  };

  const handleNewInvestmentTransactionButtonAction = (actionId: string, event: any) => {
    const fromTransactionId = investmentTransaction.investmentTransactionType === 'PURCHASE' || investmentTransaction.investmentTransactionType === 'CONVERSION' ?
      investmentTransaction.id :
      investmentTransaction.fromTransactionId;

    switch (actionId) {
      case AddNewButtonOptions.AddNew: {
        reset(INVESTMENT_TRANSACTION_PURCHASE_FORM_DEFAULT_VALUE);
        reset2(INVESTMENT_TRANSACTION_FOLLOWING_FORM_DEFAULT_VALUE);
        setSelectedInvestmentTransaction({
          investmentTransactionType: '',
          investmentTransaction: undefined,
          viewPurchaseTransactionId: fromTransactionId,
          type: InvestmentTransactionDetailsType.New,
          newView: initialPurchaseTransaction?.investmentTransactionType === 'CONVERSION' || isConverted ?
            InvestmentTransactionNewView.ConversionExcluded :
            InvestmentTransactionNewView.Following
        });
        setInvestmentTransactionType('');
      }
        break;
    }
  };

  const handleNewPortfolioCompanyButtonAction = (actionId: string, event: any) => {
    switch (actionId) {
      case AddNewButtonOptions.AddNew: {
        setSelectedPortfolioCompany({
          portfolioCompany: undefined,
          type: PortfolioCompanyDetailsType.New
        });
      }
        break;
    }
  };

  const handleConfirmDelete = async (transactionToBeDeleted: string) => {
    if (!transactionToBeDeleted ||
      (!transactionToBeDeleted && investmentTransaction.investmentTransactionType === 'PURCHASE')) {
      closeDrawer();
      setLoading(undefined);
    }
    setToBeDeleted(undefined);
    try {
      switch (investmentTransaction.investmentTransactionType) {
        case 'PURCHASE':
          await deleteInvestmentTransactionPurchase(transactionToBeDeleted);
          break;
        case 'SALE':
        case "WRITE_OFF":
          await deleteSaleTransaction(investmentTransaction.fromTransactionId, transactionToBeDeleted);
          break;
        case 'VALUATION':
          await deleteValuationTransaction(investmentTransaction.fromTransactionId, transactionToBeDeleted);
          break;
        case "SPLIT":
          await deleteStockSplitTransaction(investmentTransaction.fromTransactionId, transactionToBeDeleted);
          break;
        case "CONVERSION":
          await deleteConversionTransaction(transactionToBeDeleted);
          break;
      }
      setLoading(InvestmentTransactionCRUDStatus.Deleting);

      fetchAllInvestmentTransactions();
      informationAlert(DELETE_INVESTMENT_TRANSACTION_SUCCESS, "success");
      closeDrawer();
      setLoading(undefined);
    } catch (error) {
      informationAlert(DELETE_INVESTMENT_TRANSACTION_ERROR, "error");
      setLoading(undefined);
    }
  };

  const selectedSecurityType = watch("securityType");
  const totalAmount = watch("totalAmount");
  const quantity = watch("quantity");
  const amountPerQuantity = watch("amountPerQuantity");
  const totalAmountOther = watch2("totalAmountOther");
  const quantityOther = watch2("quantityOther");
  const amountPerQuantityOther = watch2("amountPerQuantityOther");
  const newTotalAmount = watch2("newTotalAmount");
  const newSelectedSecurityType = watch2("newSecurityType");

  return {
    loading,
    register,
    handleSubmit,
    setValue,
    errors,
    control,
    register2,
    handleSubmit2,
    errors2,
    control2,
    setValue2,
    investmentTransactionList,
    investmentTransactionType,
    initialPurchaseTransaction,
    latestValuationAmount,
    createUpdateInvestmentTransaction,
    createUpdatePurchaseTransaction,
    toggleDrawer,
    toBeDeleted,
    handleAddSingleValuation,
    handleAddMultipleValuation,
    handleChangeTransactionType,
    handleViewTransactionDetails,
    handleDelete,
    handleGoBack,
    handleCancelDelete,
    handleConfirmDelete,
    handleInvestmentTransactionNameValidation,
    handleNewInvestmentTransactionButtonAction,
    handleNewPortfolioCompanyButtonAction,
    setSelectedPurchaseTransactionId,
    closeDrawer,
    selectedSecurityType,
    selectedPurchaseTransactionId,
    showExitConfirmation,
    showApplyMultipleConfirmation,
    keepDrawerOpen,
    matchingPurchaseTransactionsText,
    isNewButtonVisible,
    newInvestmentTransactionTypeList,
    transactionNameUnavailableError,
    totalAmount,
    quantity,
    amountPerQuantity,
    totalAmountOther,
    quantityOther,
    amountPerQuantityOther,
    newTotalAmount,
    newSelectedSecurityType
  };
};
type TabProps = {
  isNew?: boolean;
};

export const useTabs = ({
  isNew = false,
}: TabProps) => {
  const [currentTab, setCurrentTab] = useState(0);

  const detailsRef = useRef<HTMLInputElement>(null);
  const transactionsRef = useRef<HTMLInputElement>(null);
  const glinfoRef = useRef<HTMLInputElement>(null);

  const handleTabChange = (event: any, newValue: any) => {
    setCurrentTab(newValue);
    switch (newValue) {
      case 0:
        detailsRef.current?.scrollIntoView({
          behavior: "smooth"
        });
        break;
      case 1:
        if (isNew) {
          glinfoRef.current?.scrollIntoView({
            behavior: "smooth"
          });
          break;
        }
        transactionsRef.current?.scrollIntoView({
          behavior: "smooth"
        });
        break;
      case 2:
        glinfoRef.current?.scrollIntoView({
          behavior: "smooth"
        });
        break;
    }
  };

  return {
    currentTab,
    detailsRef,
    transactionsRef,
    glinfoRef,
    handleTabChange
  };
};

